/**
 * S3 key → displayable URL for stored avatars and logos, from CloudFront when
 * the media session serves that kind and from presigned S3 URLs otherwise
 * (epic 2026-10-02-cloudfront-media, sub-task 04).
 *
 * The CloudFront branch makes no request at all: the URL is built from the
 * key, the browser caches the image, and a reload or a new tab shows it from
 * that cache. The presign branch is the one every caller used before, kept as
 * the fallback (the runtime switch is off, the session call failed, or the
 * stage has no CloudFront media).
 */
import { keepPreviousData, skipToken, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useMediaSession } from "./MediaSessionProvider";
import { type StoredImageKind, mediaUrlMap, servesKind } from "./mediaUrls";

export interface StoredImageUrls {
  urls: ReadonlyMap<string, string>;
  /** The presign call's error, if it failed. Always null on CloudFront. */
  error: unknown;
}

export function useStoredImageUrls(
  kind: StoredImageKind,
  s3Keys: readonly string[],
  presign: ((keys: string[]) => Promise<Record<string, string | null | undefined>>) | undefined,
  /** The presign query's cache key prefix, e.g. `"avatar-presign"`. */
  presignQueryKey: string,
  /** Per-key `?v=` for the CloudFront URL (see `mediaUrlMap`). Ignored on the
   *  presign branch: a presigned URL is new every hour anyway. */
  versions?: ReadonlyMap<string, string | number>
): StoredImageUrls {
  const { settled, session } = useMediaSession();
  const fromCloudFront = settled && servesKind(session, kind);
  const shouldPresign = settled && !fromCloudFront && presign !== undefined && s3Keys.length > 0;

  const query = useQuery({
    queryKey: [presignQueryKey, s3Keys],
    queryFn: shouldPresign && presign ? () => presign([...s3Keys]) : skipToken,
    // Presigned GETs live ~1 h; hold them 30 min so page turns and tab
    // switches reuse them.
    staleTime: 30 * 60 * 1000,
    // When the key set grows (a poll that brought a new photo), keep the URLs
    // already signed until the new answer lands — otherwise every photo on
    // screen drops to its initials for the length of a presign call.
    placeholderData: keepPreviousData,
  });

  const baseUrl = fromCloudFront ? session?.baseUrl : undefined;
  const urls = useMemo<ReadonlyMap<string, string>>(() => {
    if (baseUrl) return mediaUrlMap(baseUrl, s3Keys, versions);
    const map = new Map<string, string>();
    // Only the keys asked for: a placeholder answer may carry ones that left.
    const wanted = new Set(s3Keys);
    for (const [key, url] of Object.entries(query.data ?? {})) {
      if (url && wanted.has(key)) map.set(key, url);
    }
    return map;
  }, [baseUrl, s3Keys, versions, query.data]);

  return { urls, error: fromCloudFront ? null : query.error };
}
